/* ============================================================
   sl-hotspot.js — hotspot-by studiolab · state controller + connector engine
   VERSION: 1.7.1  ·  studiolab library (reusable)
   1.7.1 — Dot blink fix (iOS Safari): a NON-animated reposition redraw (resize /
           settle re-track / late image decode) no longer strips + re-adds the active
           dot's `.is-arrived` (clearReveals preserves it). The strip→getTotalLength
           reflow→re-add cycle was repainted mid-fade by iOS Safari as a one-frame dot
           blink right before the line re-tracked; Chrome batched it invisibly. The
           animated draw-on still hides the dot and reveals it on arrival (unchanged).
           (In-file log jumped 1.5.0→1.7.1; 1.6.0/1.7.0 were tracked via commits.)
   1.5.0 — Staged overhaul (designed via /grill-me):
           · Mobile-line fix: redraw the connector once after the entrance reveal
             settles (the reveal is a CSS transition the ResizeObserver can't see, so
             the from-load mobile line could point off into space until first click).
           · "Somente hotspots" reverse mode (config.mode='hotspots'): callouts hidden
             by default; the visible per-feature dots are the triggers; clicking one
             draws the line dot→callout (buildRoutedPath `reverse` flips the path's M)
             and the callout fades in on arrival (.is-shown) instead of the dot
             (.is-arrived). Collapsed on both breakpoints (syncBreakpoint); mobile uses
             the per-feature dot as the endpoint, not the terminus.
           · First-callout-open-on-reveal (config.defaultOpen, standard+desktop only):
             maybeDefaultOpen opens feature 0 after the entrance settles.
   1.4.0 — Connector engine rewritten as ONE orthogonal auto-wrap router
           (buildRoutedPath) for both layouts. Per-feature, per-device EXIT side
           (top/bottom/left/right) + offset along that edge (data-side/data-offset,
           resolved+fallback in Liquid). The whole card box + a section-level
           clearance (config.lineClearance) is a hard no-enter zone; the line steps
           out by the clearance then routes in right angles to the hotspot, wrapping
           around the card when the side faces away. Removed buildDesktopPath, the
           mobile left/bottom branch, data-dir-x/y, data-m-start, lineShape/lineAnchor.
   1.1.1 — 'title' anchor → title's inner edge at mid-height (line never crosses the
           callout's own text); connector re-tracks the title when the accordion
           expands/re-centers (ResizeObserver watches columns/body; reposition
           redraws are non-animated so they don't restart the stroke draw-on).
   1.1.2 — Desktop routing rebuilt (buildDesktopPath): the WHOLE callout box (title +
           body) is a forbidden region — the elbow's bend is forced BEYOND the box
           edge on the dot's side, so the line routes AROUND the content (never
           through it) and adapts automatically to any title size / amount of text.
   1.3.0 — Per-feature MOBILE line start (data-m-start: 'left' default | 'bottom').
           'bottom' exits the card's bottom-centre and drops into the hotspot;
           Direção X/Y (data-dir-x/y) still nudge the trajectory. Desktop unchanged.
           NB: 'bottom' start-X uses the STAGE centre (slide-stable), not the card's
           own rect — cards live in a translateX slider, so a card's X is offset by
           the slide / wrong mid-transition; the active card always settles centred.

   PUBLIC SURFACE (SPEC §8, followed literally):
     window.slHotspot.init(sectionId, config)
     window.slHotspot.activate(sectionId, index)   ← open a feature (exclusive)
     window.slHotspot.next(sectionId) / prev(sectionId)
   PLUS (documented extension — desktop accordion needs collapse-on-reclick):
     window.slHotspot.toggle(sectionId, index)      ← open, or collapse if already open

   The external file is loaded `defer`, but each section emits a NON-defer inline
   bootstrap that calls init() during parse — before this file runs. A queue stub
   (window.slHotspot._q) absorbs those early calls; this file drains them on load.
   init() is idempotent per section.id (Theme-Editor shopify:section:load safe).

   DECISIONS honored:
   - Collapsed on load (active = -1). Editor block-select opens that callout.
   - Exclusive accordion; clicking the open one collapses (toggle()).
   - Snappy connector: repoint + redraw on switch; fade out on collapse.
   - One geometry fn for BOTH layouts — reads real rects relative to the stage.
   - line_anchor: title | box | auto (nearest edge). Elbow or straight.
   - Mobile keeps the line + a non-interactive terminus dot. Hotspot dots desktop-only (CSS).
   - prefers-reduced-motion: final line shown instantly, no draw / jitter.
   - No external library. GSAP is detection-only (collage jitter randomisation).
   ============================================================ */
(function () {
  'use strict';

  /* load-once: multiple sections / editor reloads share one src — never
     re-bind the global document/window listeners below. */
  if (window.__slHotspotLoaded) return;
  window.__slHotspotLoaded = true;

  /* ---- adopt any queued calls from the inline bootstrap stub ---------- */
  var queued = (window.slHotspot && window.slHotspot._q) || [];

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP_MQ = '(min-width: 1024px)';

  /* instances keyed by sectionId, so re-init can tear down cleanly */
  var instances = {};

  /* ---------- helpers -------------------------------------------------- */
  function root(sectionId) { return document.getElementById('sl-section-' + sectionId); }

  function visibleStage(rootEl) {
    /* the layout whose stage is actually rendered at this breakpoint */
    var stages = rootEl.querySelectorAll('[data-hotspot-stage]');
    for (var i = 0; i < stages.length; i++) {
      if (stages[i].offsetParent !== null || stages[i].getClientRects().length) return stages[i];
    }
    return stages[0] || null;
  }

  function stageLayout(stage) {
    return stage && stage.getAttribute('data-hotspot-stage') === 'mobile' ? 'mobile' : 'desktop';
  }

  /* ---------- unified connector router (both layouts) --------------------
     The WHOLE card box (title + body) is a forbidden region — the line must
     never enter it. The line LEAVES the card from a chosen side at an offset
     along that edge, steps out by `clearance` (the magnet gap), then routes
     orthogonally (right angles only) to the hotspot, WRAPPING around the card
     when the chosen side faces away from the hotspot. One rectangle obstacle,
     so the routing is closed-form. All coords are STAGE pixel space.

       side   : 'top' | 'bottom' | 'left' | 'right'  — the edge the line exits
       offset : 0..100 — position along that edge
                 top/bottom → 0=left .. 100=right ; left/right → 0=top .. 100=bottom
       dot    : { x, y } hotspot centre (stage space)
       clearance : px the line keeps off the card (padded forbidden box)

     Guarantee: the hotspot lives on the image (beside/below the card), so it is
     always outside the padded box; every segment below is constructed to stay on
     or outside the padded box edges, so the line never crosses the card. */
  function buildRoutedPath(cardRect, stageRect, dot, side, offset, clearance, reverse) {
    var c = clearance == null ? 16 : clearance;
    var L = cardRect.left - stageRect.left, R = cardRect.right - stageRect.left;
    var T = cardRect.top - stageRect.top,  B = cardRect.bottom - stageRect.top;
    var w = R - L, h = B - T;
    var cx = (L + R) / 2, cy = (T + B) / 2;
    var PL = L - c, PR = R + c, PT = T - c, PB = B + c;   /* padded (forbidden) box */
    var o = Math.max(0, Math.min(100, isNaN(offset) ? 50 : offset)) / 100;
    var t = { x: dot.x, y: dot.y };
    var pts;

    if (side === 'left' || side === 'right') {
      var ey = T + o * h;                          /* point along the vertical edge */
      var ex = side === 'right' ? R : L;           /* card edge */
      var e2x = side === 'right' ? PR : PL;        /* padded edge (clearance out) */
      var inFront = side === 'right' ? (t.x >= PR) : (t.x <= PL);
      if (inFront) {                               /* hotspot is in front of the exit → simple L */
        pts = [[ex, ey], [e2x, ey], [t.x, ey], [t.x, t.y]];
      } else {                                     /* faces away → wrap over the nearer corner */
        var clearY = (t.y < cy) ? PT : PB;
        pts = [[ex, ey], [e2x, ey], [e2x, clearY], [t.x, clearY], [t.x, t.y]];
      }
    } else {                                       /* top | bottom */
      var ex2 = L + o * w;                          /* point along the horizontal edge */
      var ey2 = side === 'bottom' ? B : T;          /* card edge */
      var e2y = side === 'bottom' ? PB : PT;        /* padded edge */
      var inFront2 = side === 'bottom' ? (t.y >= PB) : (t.y <= PT);
      if (inFront2) {
        pts = [[ex2, ey2], [ex2, e2y], [ex2, t.y], [t.x, t.y]];
      } else {
        var clearX = (t.x < cx) ? PL : PR;
        pts = [[ex2, ey2], [ex2, e2y], [clearX, e2y], [clearX, t.y], [t.x, t.y]];
      }
    }

    /* reverse = draw FROM the hotspot TO the callout (Somente hotspots mode): same
       geometry, but the path's M moves to the dot so the stroke-draw reveals dot→card. */
    if (reverse) pts.reverse();
    var d = 'M ' + round(pts[0][0]) + ' ' + round(pts[0][1]);
    for (var i = 1; i < pts.length; i++) d += ' L ' + round(pts[i][0]) + ' ' + round(pts[i][1]);
    return d;
  }
  function round(n) { return Math.round(n * 100) / 100; }

  function hideAllDots(inst) {
    /* a dot is only shown once its line has arrived — clear them all on each
       (re)draw so no dot lingers as a lonely bubble. (In Somente-hotspots mode the
       dots stay visible via CSS; this only clears the .is-arrived reveal class.) */
    var dots = inst.root.querySelectorAll('[data-hotspot-dot], [data-hotspot-terminus]');
    for (var i = 0; i < dots.length; i++) dots[i].classList.remove('is-arrived');
  }

  function hideAllShown(inst) {
    /* Somente-hotspots: callouts/cards are hidden until their line arrives; clear the
       reveal class on each (re)draw so only the active one is shown. */
    var els = inst.root.querySelectorAll('[data-hotspot-callout], [data-hotspot-card]');
    for (var i = 0; i < els.length; i++) els[i].classList.remove('is-shown');
  }

  /* Clear the reveal state on every feature EXCEPT the ones passed to keep.
     Used by drawConnector so a NON-animated reposition can re-track the active
     feature's line WITHOUT stripping the dot/callout that is already shown —
     stripping `.is-arrived`/`.is-shown` and re-adding it within the same redraw
     forces a repaint between the two (the getTotalLength reflow), which iOS
     Safari renders as a one-frame BLINK of the dot right before the line moves.
     Preserving the active reveal removes that flash; Chrome was already flash-free
     (it batches strip+reflow+re-add into a single paint). */
  function clearReveals(inst, keepDot, keepCallout) {
    var dots = inst.root.querySelectorAll('[data-hotspot-dot], [data-hotspot-terminus]');
    for (var i = 0; i < dots.length; i++) if (dots[i] !== keepDot) dots[i].classList.remove('is-arrived');
    if (inst.config.mode === 'hotspots') {
      var els = inst.root.querySelectorAll('[data-hotspot-callout], [data-hotspot-card]');
      for (var j = 0; j < els.length; j++) if (els[j] !== keepCallout) els[j].classList.remove('is-shown');
    }
  }

  function drawConnector(inst, stage, index, animate) {
    var svg = stage.querySelector('[data-hotspot-connector]');
    if (!svg) return;
    var path = svg.querySelector('path');
    if (!path) {
      path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'none');
      svg.appendChild(path);
    }

    if (inst.dotTimer) { clearTimeout(inst.dotTimer); inst.dotTimer = null; }
    var hotspots = inst.config.mode === 'hotspots';

    if (index < 0) { /* collapsed → clear every reveal + fade the line out */
      hideAllDots(inst);
      if (hotspots) hideAllShown(inst);
      svg.style.opacity = '0';
      return;
    }

    var layout = stageLayout(stage);
    var selector = '[data-hotspot-' + (layout === 'mobile' ? 'card' : 'callout') + '="' + index + '"]';
    var callout = inst.root.querySelector(selector);
    /* Somente-hotspots uses the per-feature dot as the line endpoint on BOTH layouts
       (the visible, clickable marker); standard mobile uses the single terminus. */
    var dotEl = (layout === 'mobile' && !hotspots)
      ? stage.querySelector('[data-hotspot-terminus]')
      : stage.querySelector('[data-hotspot-dot="' + index + '"]');
    if (!callout || !dotEl) { hideAllDots(inst); if (hotspots) hideAllShown(inst); return; }

    /* Clear stale reveals on OTHER features. On a NON-animated reposition (resize /
       settle re-track / late image decode) of the active feature whose dot is
       ALREADY shown, preserve it — re-stripping then re-adding `.is-arrived` across
       the getTotalLength reflow below is what iOS Safari flashes as a dot blink
       just before the line re-tracks. An animated draw-on (animate=true) still hides
       the dot and reveals it on arrival, so the draw-then-reveal choreography stays. */
    var alreadyShown = hotspots ? callout.classList.contains('is-shown') : dotEl.classList.contains('is-arrived');
    var preserveActive = !animate && !REDUCED && alreadyShown;
    clearReveals(inst, preserveActive && !hotspots ? dotEl : null, preserveActive && hotspots ? callout : null);

    var stageRect = stage.getBoundingClientRect();
    var dotRect = dotEl.getBoundingClientRect();
    var dot = { x: dotRect.left + dotRect.width / 2 - stageRect.left, y: dotRect.top + dotRect.height / 2 - stageRect.top };

    /* per-feature, per-device EXIT side + offset (resolved + fallen-back in Liquid,
       so the rendered card/callout already carries the EFFECTIVE values for THIS
       layout). The whole card box is the forbidden region; the router wraps around
       it as needed and never crosses it. */
    var side = (callout.getAttribute('data-side') || (layout === 'mobile' ? 'bottom' : 'right')).toLowerCase();
    var offset = parseFloat(callout.getAttribute('data-offset'));
    var d = buildRoutedPath(callout.getBoundingClientRect(), stageRect, dot, side, offset, inst.config.lineClearance, hotspots);
    path.setAttribute('d', d);

    var len = path.getTotalLength();
    path.style.strokeDasharray = len + ' ' + len;
    svg.style.opacity = '1';

    /* on arrival: standard reveals the target DOT; Somente-hotspots reveals the CALLOUT
       (the dot is already visible — it was the trigger). */
    function onArrive() {
      if (hotspots) { if (callout) callout.classList.add('is-shown'); }
      else { dotEl.classList.add('is-arrived'); }
    }

    if (REDUCED || !animate) {           /* reposition (resize / accordion settle) or
                                            reduced-motion: show the final line + target
                                            instantly, no draw-on (so re-tracking the
                                            moving title never restarts the stroke). */
      path.style.transition = 'none';
      path.style.strokeDashoffset = '0';
      onArrive();
      return;
    }
    /* snappy redraw: jump to undrawn, force reflow, then draw */
    path.style.transition = 'none';
    path.style.strokeDashoffset = len + 'px';
    path.getBoundingClientRect();        /* reflow */
    path.style.transition = 'stroke-dashoffset ' + inst.config.lineSpeed + 'ms ease-in-out';
    path.style.strokeDashoffset = '0';
    /* reveal the target the moment the line reaches it */
    inst.dotTimer = setTimeout(onArrive, inst.config.lineSpeed);
  }

  function redrawActive(inst, animate) {
    if (inst.settling) return;   /* hold every draw until the layout has settled */
    var stage = visibleStage(inst.root);
    if (stage) drawConnector(inst, stage, inst.active, animate);
  }

  /* A user switch animates a layout transition that keeps the target MOVING:
     desktop accordion → the panel expands (grid-template-rows .35s) and the
     vertically-centered column re-centers as it grows, so the active title keeps
     moving; mobile slider → the track slides (transform .4s), so the active card's
     X is off-screen until it lands. Drawing the animated stroke now would aim at
     the pre-transition target and then a ResizeObserver tick would snap it elsewhere
     mid-draw ("the line changes its mind"). So hide the connector during the expand
     and draw ONCE, animated, after the panel settles — the stroke aims true and
     never re-points. A generation token cancels a stale settle if the visitor opens
     another feature first; a timeout backs up the transitionend. */
  function settleThenDraw(inst, index) {
    var gen = (inst.settleGen = (inst.settleGen || 0) + 1);
    inst.settling = true;
    var stage = visibleStage(inst.root);
    var svg = stage && stage.querySelector('[data-hotspot-connector]');
    if (svg) svg.style.opacity = '0';
    if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
    /* Watch the transition that is actually moving the geometry: on desktop the
       opening callout's panel (grid-template-rows .35s, the re-centering title);
       on mobile the slider track (transform .4s — a card's X is wrong until the
       slide finishes, which sent the line "from beyond" off-screen). */
    var desktop = window.matchMedia(DESKTOP_MQ).matches;
    var watchEl, prop;
    if (desktop) {
      var callout = inst.root.querySelector('[data-hotspot-callout="' + index + '"]');
      watchEl = callout && callout.querySelector('[class*="__panel"]');
      prop = 'grid-template-rows';
    } else {
      watchEl = inst.root.querySelector('[data-hotspot-track]');
      prop = 'transform';
    }
    function finish() {
      if (watchEl) watchEl.removeEventListener('transitionend', onEnd);  /* always — no leak */
      if (gen !== inst.settleGen) return;            /* superseded: don't touch shared state */
      if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
      inst.settling = false;
      redrawActive(inst, true);
    }
    function onEnd(e) { if (e.target === watchEl && e.propertyName === prop) finish(); }
    if (watchEl) watchEl.addEventListener('transitionend', onEnd);
    inst.settleTimer = setTimeout(finish, 480);      /* > .4s track / .35s panel; backs up transitionend */
  }

  /* mobile is a slider (no collapsed state) — ensure a feature is active there;
     desktop keeps the collapsed-on-load state. Called at init + on resize. */
  function syncBreakpoint(inst) {
    var desktop = window.matchMedia(DESKTOP_MQ).matches;
    /* Somente-hotspots: both breakpoints start collapsed — the visible hotspot markers
       are the triggers, so there's no auto-active feature on either layout. */
    if (inst.config.mode === 'hotspots') { redrawActive(inst); return; }
    /* standard: mobile is a slider (no collapsed state) → ensure feature 0 is active;
       desktop stays collapsed unless default-open is requested (applied on reveal). */
    if (!desktop && inst.active < 0 && inst.count > 0) setActive(inst, 0);
    else redrawActive(inst);
  }

  /* standard + desktop + default-open opt-in: auto-open feature 0 once the section
     reveals (called from scheduleRevealRedraw, so it opens after the entrance settles
     instead of at init). No-op in hotspots mode, on mobile, or if already open. */
  function maybeDefaultOpen(inst) {
    if (inst.config.mode !== 'standard' || !inst.config.defaultOpen) return;
    if (!window.matchMedia(DESKTOP_MQ).matches) return;
    if (inst.active < 0 && inst.count > 0) setActive(inst, 0, true);
  }

  /* ---------- state ---------------------------------------------------- */
  function setActive(inst, index, defer) {
    inst.active = index;

    /* exclusive .is-active on every callout / card / dot for this index */
    var all = inst.root.querySelectorAll('[data-feature-index]');
    for (var i = 0; i < all.length; i++) {
      var idx = parseInt(all[i].getAttribute('data-feature-index'), 10);
      var on = idx === index;
      all[i].classList.toggle('is-active', on);
      if (all[i].hasAttribute('aria-expanded')) all[i].setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    /* image swap (per visible layout) + mobile track position */
    var stages = inst.root.querySelectorAll('[data-hotspot-stage]');
    for (var s = 0; s < stages.length; s++) swapImage(inst, stages[s], index);

    var track = inst.root.querySelector('[data-hotspot-track]');
    if (track && index >= 0) {
      /* each step shifts by one card (100%) PLUS the inter-slide gap (px) */
      var slideGap = parseFloat(track.getAttribute('data-gap')) || 0;
      track.style.transform = 'translateX(calc(' + (-index * 100) + '% - ' + (index * slideGap) + 'px))';
    }

    /* mobile shows one terminus dot — move it to the active feature's hotspot */
    if (index >= 0) {
      var term = inst.root.querySelector('[data-hotspot-terminus]');
      var card = inst.root.querySelector('[data-hotspot-card="' + index + '"]');
      if (term && card) {
        term.style.left = card.getAttribute('data-hx') + '%';
        term.style.top = card.getAttribute('data-hy') + '%';
      }
    }

    /* A USER open/switch (defer=true) waits out its layout transition before the
       animated stroke draws — desktop panel expand OR mobile slider slide (see
       settleThenDraw) — so the line draws once to the settled target. Collapse
       (index < 0), reduced-motion, and the init/reveal activation (defer falsy,
       held by the settling guard until scheduleRevealRedraw) draw immediately. */
    if (index >= 0 && !REDUCED && defer) {
      settleThenDraw(inst, index);
    } else {
      redrawActive(inst, true);   /* a user switch draws the line on; resize/settle redraws don't */
    }
  }

  function swapImage(inst, stage, index) {
    var imgs = stage.querySelectorAll('[data-hotspot-img]');
    var feat = index >= 0 ? inst.root.querySelector('[data-feature-index="' + index + '"][data-has-image="true"]') : null;
    var target = feat ? index.toString() : 'main';
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].style.opacity = imgs[i].getAttribute('data-hotspot-img') === target ? '1' : '0';
    }
  }

  /* ---------- public actions ------------------------------------------- */
  function activate(sectionId, index) {
    var inst = instances[sectionId];
    if (!inst) return;
    index = parseInt(index, 10);
    if (isNaN(index)) return;
    setActive(inst, index, true);
  }

  function toggle(sectionId, index) {           /* desktop accordion click */
    var inst = instances[sectionId];
    if (!inst) return;
    index = parseInt(index, 10);
    setActive(inst, inst.active === index ? -1 : index, true);
  }

  function step(sectionId, dir) {               /* mobile arrows — wrap */
    var inst = instances[sectionId];
    if (!inst || !inst.count) return;
    var from = inst.active < 0 ? 0 : inst.active;
    var next = (from + dir + inst.count) % inst.count;
    setActive(inst, next, true);
  }
  function next(sectionId) { step(sectionId, 1); }
  function prev(sectionId) { step(sectionId, -1); }

  /* ---------- init / teardown ----------------------------------------- */
  function teardown(inst) {
    if (inst.io) inst.io.disconnect();
    if (inst.ro) inst.ro.disconnect();
    if (inst.revealTimer) { clearTimeout(inst.revealTimer); inst.revealTimer = null; }
    if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
    inst.settleGen = (inst.settleGen || 0) + 1;   /* cancel any pending settle finish */
    inst.settling = false;
  }

  /* The entrance reveal is a pure CSS transition (opacity/transform) — the
     ResizeObserver can't see it, so a connector drawn DURING the reveal can end
     up stale: notably on mobile, where a feature is active from load and the card
     + image-stage settle at staggered times, leaving the first line pointing off
     into space until the visitor clicks. Redraw once (non-animated) after the
     reveal has fully settled. Desktop is unaffected (it loads collapsed, active
     = -1, so no line is drawn until a click). */
  function scheduleRevealRedraw(inst) {
    var r = (inst.config && inst.config.reveal) || {};
    var settle = (r.delay || 0) + (r.duration || 600) + ((inst.count || 0) * (r.stagger || 0)) + 80;
    if (inst.revealTimer) clearTimeout(inst.revealTimer);
    inst.revealTimer = setTimeout(function () {
      inst.settling = false;    /* layout has settled — release the draw hold */
      redrawActive(inst, false);
      maybeDefaultOpen(inst);   /* desktop standard default-open opens AFTER the entrance */
    }, settle);
  }

  function init(sectionId, config) {
    var rootEl = root(sectionId);
    if (!rootEl) return;

    if (instances[sectionId]) teardown(instances[sectionId]);   /* idempotent re-init */

    var inst = {
      root: rootEl,
      config: {
        lineSpeed: (config && config.lineSpeed) || 1000,
        lineClearance: (config && config.lineClearance != null) ? config.lineClearance : 16,
        textAlign: (config && config.textAlign) || 'left',
        imageMotion: (config && config.imageMotion) || 'none',
        mode: (config && config.mode) || 'standard',
        defaultOpen: !!(config && config.defaultOpen),
        reveal: (config && config.reveal) || {}
      },
      active: -1,
      count: rootEl.querySelectorAll('[data-hotspot-callout]').length ||
             rootEl.querySelectorAll('[data-hotspot-card]').length
    };
    instances[sectionId] = inst;

    /* Hold every connector draw until the entrance reveal settles. The first
       paint runs against un-laid-out geometry (especially mobile, where a feature
       is active from load and the card/stage settle at staggered times), which
       left the line pointing "from beyond" until the visitor tapped. With the hold
       on, scheduleRevealRedraw clears it and draws once, against settled geometry. */
    inst.settling = true;

    /* entrance reveal — fire once, then disconnect (does NOT auto-open).
       In the Theme Editor the section re-renders on every setting change, which
       resets it to the hidden pre-reveal state; a fresh IntersectionObserver on
       an already-scrolled section can't be relied on to re-fire, so the operator
       loses sight of what they're editing until a full save. Skip the gate
       entirely in designMode — the editor must always show the section. */
    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    if (!inEditor && 'IntersectionObserver' in window && !rootEl.classList.contains('is-revealed')) {
      inst.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            rootEl.classList.add('is-revealed');
            teardownIO(inst);
            scheduleRevealRedraw(inst);
            break;
          }
        }
      }, { threshold: 0.2 });
      inst.io.observe(rootEl);
    } else {
      rootEl.classList.add('is-revealed');
      scheduleRevealRedraw(inst);
    }

    /* keep the active line glued to its dot through resize / image reflow */
    if ('ResizeObserver' in window) {
      /* observe stages AND columns/body: opening a callout grows its column and —
         with vertical centering — re-centers it, moving the active title; the
         connector must re-track to it. These redraws are non-animated (reposition). */
      inst.ro = new ResizeObserver(function () { redrawActive(inst, false); });
      var observed = rootEl.querySelectorAll('[data-hotspot-stage], [data-hotspot-col], [data-hotspot-body]');
      for (var s = 0; s < observed.length; s++) inst.ro.observe(observed[s]);
    }

    /* final safety net: a late image decode can shift the stage after the reveal
       has settled — redraw the active line once everything has loaded. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { redrawActive(inst, false); }, { once: true });
    }

    /* GSAP is OPTIONAL — only used to randomise collage jitter, never loaded */
    if (inst.config.imageMotion === 'collage' && !REDUCED && window.gsap) {
      randomiseCollage(rootEl, window.gsap);
    }

    syncBreakpoint(inst);   /* mobile → activate feature 0; desktop → stay collapsed */
  }

  function teardownIO(inst) { if (inst.io) { inst.io.disconnect(); inst.io = null; } }

  function randomiseCollage(rootEl, G) {
    var frames = rootEl.querySelectorAll('[data-hotspot-stage] [data-hotspot-img="main"]');
    for (var i = 0; i < frames.length; i++) {
      frames[i].style.animation = 'none';          /* hand off from the CSS keyframes */
      var rot = (Math.random() * 2 - 1) * 1.4;     /* ±1.4deg */
      var dx = (Math.random() * 2 - 1) * 6;
      G.fromTo(frames[i],
        { x: dx, y: -10, rotation: rot, opacity: 0 },
        { x: 0, y: 0, rotation: rot * 0.3, opacity: 1, duration: 0.7, ease: 'steps(6)' });
    }
  }

  /* ---------- Theme Editor (designMode) ------------------------------- */
  function featureIndexFromBlock(rootEl, blockId) {
    var el = rootEl.querySelector('[data-block-id="' + blockId + '"]');
    return el ? parseInt(el.getAttribute('data-feature-index'), 10) : -1;
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var inst = findInst(e.target);
      if (!inst) return;
      var idx = featureIndexFromBlock(inst.root, e.detail && e.detail.blockId);
      if (idx >= 0) setActive(inst, idx, true);   /* editor select = user open → defer */
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      var inst = findInst(e.target);
      if (inst) setActive(inst, -1);            /* back to the clean state */
    });
  }

  function findInst(node) {
    var rootEl = node && node.closest ? node.closest('[id^="sl-section-"]') : null;
    if (!rootEl) return null;
    var id = rootEl.id.replace('sl-section-', '');
    return instances[id] || null;
  }

  /* re-init on section (re)load in the editor */
  document.addEventListener('shopify:section:load', function (e) {
    var rootEl = e.target.querySelector ? e.target.querySelector('[data-sl-hotspot]') : null;
    if (rootEl) {
      var id = rootEl.id.replace('sl-section-', '');
      /* config travels via the section's own inline bootstrap, which re-runs
         on load; this listener just guarantees stale observers are dropped. */
      if (instances[id]) teardown(instances[id]);
    }
  });

  /* redraw on viewport resize (breakpoint swap desktop ↔ mobile) */
  var rafPending = false;
  window.addEventListener('resize', function () {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      for (var id in instances) if (instances.hasOwnProperty(id)) syncBreakpoint(instances[id]);
    });
  });

  /* ---------- publish + drain the bootstrap queue --------------------- */
  var API = { _q: [], init: init, activate: activate, toggle: toggle, next: next, prev: prev };
  window.slHotspot = API;
  queued.forEach(function (call) { if (API[call[0]]) API[call[0]].apply(API, call.slice(1)); });
})();
